import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, Chip, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell,
  CircularProgress, Snackbar, Alert,
} from '@mui/material';
import {
  History as HistoryIcon,
  ArrowBack as BackIcon,
  DeleteOutline as DeleteIcon,
  Lock as LockIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { dreService, DreSnapshot, formatBRL } from '../../services/dreService';

const DreSnapshots: React.FC = () => {
  const navigate = useNavigate();
  const [snapshots, setSnapshots] = useState<DreSnapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dreService.listSnapshots();
      setSnapshots(data);
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar snapshots');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (s: DreSnapshot) => {
    if (s.is_locked) {
      setSnackbar({ open: true, message: 'Snapshot travado não pode ser removido', severity: 'error' });
      return;
    }
    if (!window.confirm(`Excluir snapshot ${s.period_label}?`)) return;
    try {
      await dreService.deleteSnapshot(s.id);
      setSnackbar({ open: true, message: 'Snapshot removido', severity: 'success' });
      fetchData();
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Erro', severity: 'error' });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap: 1.5 }}>
      <Paper
        elevation={0}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2, py: 1, borderRadius: 2,
          bgcolor: '#fff', border: '1px solid #e2e8f0',
        }}
      >
        <IconButton size="small" onClick={() => navigate('/financial/dre')}>
          <BackIcon />
        </IconButton>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5,
          bgcolor: '#0C6FC1', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <HistoryIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: 1, lineHeight: 1 }}>
            DRE Gerencial
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>
            Snapshots históricos
          </Typography>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{ flex: 1, minHeight: 0, overflow: 'auto', borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}
      >
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={32} sx={{ color: '#0C6FC1' }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : snapshots.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Nenhum snapshot ainda. Salve um a partir da página DRE.
            </Typography>
          </Box>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 800, fontSize: '0.7rem', color: '#475569', bgcolor: '#f8fafc', textTransform: 'uppercase', letterSpacing: 0.5 } }}>
                <TableCell>Período</TableCell>
                <TableCell>Regime</TableCell>
                <TableCell>Unidade</TableCell>
                <TableCell align="right">Receita</TableCell>
                <TableCell align="right">Margem Contrib.</TableCell>
                <TableCell align="right">Lucro Op.</TableCell>
                <TableCell align="right">Resultado Líq.</TableCell>
                <TableCell>Criado em</TableCell>
                <TableCell width={80}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {snapshots.map((s) => (
                <TableRow key={s.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{s.period_label}</Typography>
                    <Typography sx={{ fontSize: '0.66rem', color: '#94a3b8' }}>
                      {s.period_start} → {s.period_end}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={s.regime} size="small" sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700, textTransform: 'uppercase' }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.78rem' }}>
                    {s.consolidated ? (
                      <Chip label={`${s.unit_ids?.length || 0} unidade(s)`} size="small" sx={{ fontSize: '0.65rem', bgcolor: '#dbeafe', color: '#1e40af' }} />
                    ) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700 }}>{formatBRL(s.total_revenue)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700 }}>{formatBRL(s.total_contribution_margin)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700 }}>{formatBRL(s.total_operating_profit)}</TableCell>
                  <TableCell align="right" sx={{
                    fontFamily: '"JetBrains Mono", monospace', fontWeight: 800,
                    color: (s.total_net_result || 0) < 0 ? '#dc2626' : '#15803d',
                  }}>
                    {formatBRL(s.total_net_result)}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', color: '#64748b' }}>
                    {new Date(s.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.3 }}>
                      {s.is_locked && (
                        <Tooltip title="Travado">
                          <LockIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                        </Tooltip>
                      )}
                      <Tooltip title="Excluir">
                        <span>
                          <IconButton size="small" onClick={() => handleDelete(s)} disabled={s.is_locked}>
                            <DeleteIcon fontSize="small" sx={{ color: s.is_locked ? '#cbd5e1' : '#dc2626' }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DreSnapshots;
